import { CheckCircle2, Clock3, Download, FileText, History, Trash2, Wallet, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { confirmModal } from '@/components/ui/modal'
import { HR_PAYMENT_WRITE_PERM } from '@/features/hr/permissions'
import { apiErrorMessage } from '@/i18n'
import { useAuth } from '@/lib/auth/AuthProvider'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { fmtDate, fmtMoney } from '@/features/shared/format'
import { Select } from '@/components/ui/select'
import { useDeleteHistoricalPayment } from '../api'
import { GecmisOdemeModal } from '../components/actions/GecmisOdemeModal'
import { KpiCard } from '../components/KpiCard'
import { MobileList } from '../components/MobileList'
import { KvRow, SectionCard } from '../components/SectionCard'
import { OdemeStatusPill } from '../components/StatusPill'
import { TABLE_FRAME } from '../components/tableFrame'
import type { PersonelDetail } from '../model'
import { exportOdemeCsv, printDekont } from '../lib/exportActions'
import type { OdemeTur, Personel } from '../types'

export function OdemeGecmisiTab({ personel, odeme }: { personel: Personel; odeme: PersonelDetail['odeme'] }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const canWrite = can(HR_PAYMENT_WRITE_PERM)
  const remove = useDeleteHistoricalPayment()
  const [adding, setAdding] = useState(false)
  const [tur, setTur] = useState<'all' | OdemeTur>('all')
  const years = useMemo(() => [...new Set(odeme.liste.map((o) => o.tarih.slice(0, 4)))], [odeme.liste])
  const [yil, setYil] = useState(years[0] ?? '')
  const [selected, setSelected] = useState<(typeof odeme.liste)[number] | null>(odeme.liste[0] ?? null)

  const rows = useMemo(
    () => odeme.liste.filter((o) => o.tarih.startsWith(yil) && (tur === 'all' || o.tur === tur)),
    [odeme.liste, tur, yil],
  )

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <KpiCard
          label={t('hr.personel.odemeGecmis.yil')}
          value={fmtMoney(odeme.yilToplam)}
          hint={`${odeme.yilAdet} ${t('hr.personel.odemeGecmis.odeme')}`}
          tint="success"
          icon={<Wallet className="size-5" />}
        />
        <KpiCard
          label={t('hr.personel.odemeGecmis.bekleyen')}
          value={fmtMoney(odeme.bekleyen)}
          hint={`${odeme.bekleyenAdet} ${t('hr.personel.odemeGecmis.odeme')}`}
          tint="warning"
          icon={<Clock3 className="size-5" />}
        />
        <KpiCard
          label={t('hr.personel.odemeGecmis.avans')}
          value={fmtMoney(odeme.avans)}
          hint={`${odeme.avansAdet} ${t('hr.personel.odemeGecmis.aktifAvans')}`}
          tint="info"
          icon={<FileText className="size-5" />}
        />
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)] gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
        <SectionCard
          title={t('hr.personel.odemeGecmis.title')}
          actionClassName="max-sm:w-full"
          action={
            <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
              <Select
                size="lg"
                className="w-full min-w-0 rounded-xl sm:w-auto sm:min-w-[9.5rem]"
                value={yil}
                onChange={setYil}
                options={years.map((y) => ({ value: y, label: y }))}
              />
              <Select
                size="lg"
                className="w-full min-w-0 rounded-xl sm:w-auto sm:min-w-[9.5rem]"
                value={tur}
                onChange={(v) => setTur(v as 'all' | OdemeTur)}
                options={[
                  { value: 'all', label: t('hr.personel.odemeGecmis.tum') },
                  ...(['maas', 'avans', 'prim', 'yemek', 'ulasim', 'masraf'] as const).map((x) => ({ value: x, label: t(`hr.personel.odeme.tur.${x}`) })),
                ]}
              />
              {canWrite ? (
                <Button variant="outline" size="sm" className="col-span-2 h-10 sm:h-9" onClick={() => setAdding(true)}>
                  <History />
                  {t('hr.personel.gecmisOdeme.open')}
                </Button>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                className="col-span-2 h-10 sm:h-9"
                disabled={rows.length === 0}
                onClick={() => exportOdemeCsv(`odemeler_${personel.sicil}_${yil}.csv`, rows)}
              >
                <Download />
                {t('hr.personel.export')}
              </Button>
            </div>
          }
          noPad
        >
          <MobileList
            rows={rows.map((o) => ({
              key: o.id,
              title: o.aciklama,
              subtitle: `${fmtDate(o.tarih)} · ${t(`hr.personel.odeme.tur.${o.tur}`)}`,
              trailing: <span className="text-sm font-semibold tabular-nums">{fmtMoney(o.tutar)}</span>,
              meta: (
                <>
                  <OdemeStatusPill durum={o.durum} />
                  <span>{o.yontem}</span>
                </>
              ),
              selected: selected?.id === o.id,
              onClick: () => setSelected(o),
            }))}
          />
          <Table className="hr-table" wrapperClassName={TABLE_FRAME}>
            <THead>
              <TR>
                <TH>{t('hr.personel.odemeGecmis.tarih')}</TH>
                <TH>{t('hr.personel.odemeGecmis.tur')}</TH>
                <TH>{t('hr.personel.odemeGecmis.aciklama')}</TH>
                <TH className="text-right">{t('hr.personel.odemeGecmis.tutar')}</TH>
                <TH>{t('hr.personel.odemeGecmis.yontem')}</TH>
                <TH>{t('hr.personel.columns.durum')}</TH>
              </TR>
            </THead>
            <TBody>
              {rows.map((o) => (
                <TR
                  key={o.id}
                  data-selected={selected?.id === o.id ? 'true' : undefined}
                  className="cursor-pointer"
                  onClick={() => setSelected(o)}
                >
                  <TD className="whitespace-nowrap text-muted-foreground">{fmtDate(o.tarih)}</TD>
                  <TD>{t(`hr.personel.odeme.tur.${o.tur}`)}</TD>
                  <TD className="font-medium">
                    {o.aciklama}
                    {o.gecmisAktarim ? (
                      <Badge tone="info" className="ml-2">
                        {t('hr.personel.gecmisBordro.badge')}
                      </Badge>
                    ) : null}
                  </TD>
                  <TD className="text-right tabular-nums font-medium">{fmtMoney(o.tutar)}</TD>
                  <TD className="text-muted-foreground">{o.yontem}</TD>
                  <TD>
                    <OdemeStatusPill durum={o.durum} />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </SectionCard>

        <SectionCard
          title={t('hr.personel.odemeGecmis.detay')}
          action={
            selected ? (
              <Button variant="ghost" size="icon" className="size-8" aria-label={t('common.close')} onClick={() => setSelected(null)}>
                <X className="size-4" />
              </Button>
            ) : null
          }
        >
          {selected ? (
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-success/12 p-2.5 text-success">
                  <Wallet className="size-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">{selected.aciklama}</div>
                  <div className="text-xs text-muted-foreground">
                    {t(`hr.personel.odeme.tur.${selected.tur}`)} {t('hr.personel.odemeGecmis.odeme').toLowerCase()}
                  </div>
                </div>
                <OdemeStatusPill durum={selected.durum} />
              </div>

              <div>
                <KvRow label={t('hr.personel.odemeGecmis.tarih')} value={fmtDate(selected.tarih)} />
                <KvRow label={t('hr.personel.odemeGecmis.tutar')} value={fmtMoney(selected.tutar)} />
                <KvRow label={t('hr.personel.odemeGecmis.yontem')} value={selected.yontem} />
                <KvRow label={t('hr.personel.ucret.donem')} value={selected.donem} />
              </div>

              {selected.gecmisAktarim ? (
                <div className="flex items-center justify-between gap-2 rounded-lg border border-info/30 bg-info/5 px-3 py-2 text-xs text-muted-foreground">
                  <span>{t('hr.personel.gecmisOdeme.importedNote')}</span>
                  {canWrite ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      disabled={remove.isPending}
                      onClick={() =>
                        void confirmModal({
                          title: t('hr.personel.gecmisOdeme.deleteTitle'),
                          description: t('hr.personel.gecmisOdeme.deleteBody', { period: selected.donem, amount: fmtMoney(selected.tutar) }),
                          confirmLabel: t('common.delete'),
                          destructive: true,
                        }).then((ok) => {
                          if (!ok) return
                          remove.mutate(
                            { employeeId: personel.id, paymentId: selected.id },
                            {
                              onSuccess: () => {
                                toast.success(t('hr.personel.gecmisOdeme.deleted'))
                                setSelected(null)
                              },
                              onError: (e) => toast.error(apiErrorMessage(e)),
                            },
                          )
                        })
                      }
                    >
                      <Trash2 />
                      {t('common.delete')}
                    </Button>
                  ) : null}
                </div>
              ) : null}

              <div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm">
                <div className="flex items-center gap-2">
                  <FileText className="size-4 text-muted-foreground" />
                  <button type="button" className="font-medium text-primary hover:underline" onClick={() => printDekont(selected, odeme.banka.iban)}>
                    dekont_{selected.tarih.replaceAll('-', '')}.pdf
                  </button>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">124 KB</div>
              </div>

              <div>
                <div className="mb-2 text-sm font-medium">{t('hr.personel.odemeGecmis.onay')}</div>
                <div className="space-y-2">
                  {odeme.timeline.map((s) => (
                    <div key={s.t} className="flex gap-2 text-sm">
                      <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-success" />
                      <div>
                        <div className="font-medium">{s.b}</div>
                        <div className="text-xs text-muted-foreground">{s.t}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border p-3">
                <div className="text-xs text-muted-foreground">{t('hr.personel.odemeGecmis.banka')}</div>
                <div className="mt-1 text-sm font-medium">{odeme.banka.ad}</div>
                <div className="mt-1 font-mono text-xs text-muted-foreground">{odeme.banka.iban}</div>
                <div className="mt-2">
                  <Badge tone="success">{t('hr.personel.durum.aktif')}</Badge>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t('hr.personel.odemeGecmis.sec')}</p>
          )}
        </SectionCard>
      </div>
      {adding ? <GecmisOdemeModal personelId={personel.id} onClose={() => setAdding(false)} /> : null}
    </div>
  )
}
